// The SLO preset's numbers as its rule page and the Alerts list show them (D119; APS-279 step 4b's
// contract v1). The evaluator measures; this only reads what it stored: a pair's budget spent at
// its rate (the mock's "2.1 % in 1 h"), the pair closest to firing, the severity that wins, and
// the budget left as a percent, overspent shown at 0.
import type { AlertSlo, SloPair, SloWindow } from '../api/types'
import type { Translator } from '../i18n'

export const WINDOW_MINUTES: Record<SloWindow, number> = {
  '5m': 5,
  '30m': 30,
  '1h': 60,
  '6h': 360,
  '3d': 4320,
}

/**
 * The budget a pair spends at its rate over its long window, in percent of the whole budget:
 * k · long / window. Over 28 days, 14.4× for 1 h is 2.14 %, 6× for 6 h 5.36 %, 1× for 3 d 10.7 %.
 */
export function spentAtRate(rate: number, long: SloWindow, windowDays: number): number {
  return ((rate * WINDOW_MINUTES[long]) / (windowDays * 24 * 60)) * 100
}

/** Days until the budget runs out at `burn`: left · window / burn (38 % at 1.2× over 28 d: 8.87). */
export function daysLeft(left: number, windowDays: number, burn: number): number | null {
  return burn > 0 && left > 0 ? (left * windowDays) / burn : null
}

/** How close a pair is to firing: both its windows must burn above its rate, so the lower one. */
export function closeness(p: SloPair): number | null {
  if (p.burn_long === null || p.burn_short === null || p.rate <= 0) return null
  return Math.min(p.burn_long, p.burn_short) / p.rate
}

/** The pair closest to firing (the one `last_value` ranks the rule by); none when none is measured. */
export function closestPair(slo: Pick<AlertSlo, 'pairs'>): SloPair | undefined {
  let best: { p: SloPair; c: number } | undefined
  for (const p of slo.pairs) {
    const c = closeness(p)
    if (c !== null && (!best || c > best.c)) best = { p, c }
  }
  return best?.p
}

/** The severity a firing SLO shows: page when its page pair fires, else ticket; null when none fires. */
export function firingSeverity(slo: Pick<AlertSlo, 'pairs'>): 'page' | 'ticket' | null {
  const firing = slo.pairs.filter((p) => p.state === 'firing')
  if (firing.some((p) => p.severity === 'page')) return 'page'
  return firing.length > 0 ? 'ticket' : null
}

/** The budget left as a percent: an overspent budget (left under 0) shows 0 and says so. */
export function leftPercent(left: number | null): { value: number; overspent: boolean } | null {
  if (left === null) return null
  return { value: Math.max(0, left * 100), overspent: left < 0 }
}

/** A burn as the mock writes it: 1.3×, 14.4×, 6×. */
export function formatBurn(i18n: Pick<Translator, 'number'>, burn: number): string {
  return `${i18n.number(burn, { maximumFractionDigits: burn < 10 ? 2 : 1 })}×`
}
